import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Grid, TableCell, CircularProgress } from '@material-ui/core';
import {
  useTableStyles,
  NewListTable
} from 'components/Crud/ListTable/ListTable';
import ListTableRow from 'components/Crud/ListTable/ListTableRow';
import useCrudStyles from 'components/Crud/styles';
import { useEngineersState } from 'store/selectors';
import { fetchEngineersByIds } from 'store/slices/engineers';
import Typography from 'components/Typography';

const UNAVAILABLE_REASON_MESSAGE: Record<string, string> = {
  SCHEDULE_ERROR: 'Exceeds Schedule',
  MEETING_OVERLAP: 'Meeting Overlap',
  SHIFT_OVERLAP: 'Overtime'
};

const columnsWidth = [240, 160, 180, 180, 164, 120];
const headers = [
  'Name',
  'Company',
  'Reason',
  'Team Manager',
  'Postcode',
  'Phone'
];

function UnavailableEngineersView() {
  const tableClasses = useTableStyles();
  const crudClasses = useCrudStyles();
  const location = useLocation();
  const dispatch = useDispatch();

  const params = new URLSearchParams(location.search);
  const idsString = params.get('ids') || '';
  const failingEngineerIds = idsString ? idsString.split(',') : [];

  const reasons = params.get('reasons')?.split(',') || [];
  const reasonByEngineerId = failingEngineerIds.reduce<Record<string, string>>(
    (acc, id, index) => {
      acc[id] = reasons[index];
      return acc;
    },
    {}
  );

  const { items: engineers, isLoading } = useEngineersState();

  useEffect(() => {
    if (failingEngineerIds.length === 0) {
      return;
    }
    dispatch(fetchEngineersByIds(failingEngineerIds));
  }, [idsString]);

  return (
    <Grid
      container
      className={crudClasses.listContainer}
      data-testid="unavailableEngineersContainer"
    >
      <Grid item xs={12}>
        <section className={tableClasses.tableContainer}>
          <div className={tableClasses.tableHeader}>
            <Typography variant="h1">Unavailable Engineers</Typography>
          </div>
          {isLoading ? (
            <div
              className={crudClasses.loadingWrapper}
              data-testid="unavailableEngineersLoading"
            >
              <CircularProgress className={crudClasses.spinnerWrapper} />
            </div>
          ) : (
            <NewListTable
              label="A table of unavailable engineers"
              columnsWidth={columnsWidth}
              domainItems={engineers}
              headers={headers}
              actionsColumn={false}
            >
              {engineers.map(engineer => (
                <ListTableRow
                  key={engineer.id}
                  data-testid={`tableRow-${engineer.id}`}
                >
                  <TableCell
                    className={tableClasses.tableCell}
                    data-testid={`tableCell-${engineer.id}-name`}
                  >
                    {`${engineer.firstName} ${engineer.lastName}`}
                  </TableCell>
                  <TableCell
                    className={tableClasses.tableCell}
                    data-testid={`tableCell-${engineer.id}-company`}
                  >
                    {engineer.company}
                  </TableCell>
                  <TableCell
                    className={tableClasses.tableCell}
                    data-testid={`tableCell-${engineer.id}-reason`}
                  >
                    {UNAVAILABLE_REASON_MESSAGE[
                      reasonByEngineerId[engineer.id]
                    ] || 'Other'}
                  </TableCell>
                  <TableCell
                    className={tableClasses.tableCell}
                    data-testid={`tableCell-${engineer.id}-managerName`}
                  >
                    {engineer.team?.managerName}
                  </TableCell>
                  <TableCell
                    className={tableClasses.tableCell}
                    data-testid={`tableCell-${engineer.id}-postcode`}
                  >
                    {engineer.postcode}
                  </TableCell>
                  <TableCell
                    className={tableClasses.tableCell}
                    data-testid={`tableCell-${engineer.id}-phoneNumber`}
                  >
                    {engineer.phoneNumber}
                  </TableCell>
                </ListTableRow>
              ))}
            </NewListTable>
          )}
        </section>
      </Grid>
    </Grid>
  );
}

export default UnavailableEngineersView;
